import { useState, useCallback, useRef, useEffect } from 'react';

interface EditableFieldProps {
  initialValue?: string;
  fieldName: string;
  onUpdate: (value: string) => Promise<any>; // Allow any return type from API
  validate?: (value: string) => string | undefined;
  isVisible?: boolean;
}

interface EditableFieldReturn {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  value: string;
  setValue: (value: string) => void;
  isLoading: boolean;
  error: string;
  startEdit: () => void;
  handleSubmit: (e?: React.FormEvent, currentValue?: string) => Promise<void>;
  isVisible: boolean;
  resetField: () => void;
}

/**
 * A hook for managing editable fields with validation and API updates
 */
export const useEditableField = ({
  initialValue = '',
  fieldName,
  onUpdate,
  validate,
  isVisible = true
}: EditableFieldProps): EditableFieldReturn => {
  // Track previous initial value to detect actual changes
  const previousInitialValueRef = useRef<string>(initialValue);
  
  // Core state
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [value, setValue] = useState<string>(initialValue);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  
  // Properly sync value with initialValue when it changes
  useEffect(() => {
    // Only update if initialValue changed and we're not currently editing
    if (
      initialValue !== undefined && 
      previousInitialValueRef.current !== initialValue && 
      !isEditing
    ) {
      setValue(initialValue);
      previousInitialValueRef.current = initialValue;
    }
  }, [initialValue, isEditing]);

  // Clean up edit mode when visibility changes
  useEffect(() => {
    if (!isVisible && isEditing) {
      setIsEditing(false);
      setValue(previousInitialValueRef.current); // Reset to last known good value
    }
  }, [isVisible, isEditing]);

  // Reset all state to initial values
  const resetField = useCallback((): void => {
    if (initialValue !== undefined) {
      setValue(initialValue);
      previousInitialValueRef.current = initialValue;
    }
    setIsEditing(false);
    setIsLoading(false);
    setError('');
  }, [initialValue]);

  // Start editing with current value
  const startEdit = useCallback((): void => {
    setIsEditing(true);
    setError('');
  }, []);

  // Handle value changes with minimal logging
  const setValueAndLog = useCallback((newValue: string): void => {
    setValue(newValue);
  }, []);

  // Handle form submission and API updates
  const handleSubmit = useCallback(async (
    e?: React.FormEvent, 
    currentValue?: string
  ): Promise<void> => {
    e?.preventDefault();
    
    // Use provided value or fall back to state
    const valueToSubmit = currentValue !== undefined ? currentValue : value;
    
    // Apply character limits based on field name
    let maxLength = 0;
    if (fieldName.toLowerCase() === 'display name') {
      maxLength = 20;
    } else if (fieldName.toLowerCase() === 'tagline') {
      maxLength = 50;
    } else if (fieldName.toLowerCase() === 'description') {
      maxLength = 6250;
    } else if (fieldName.toLowerCase() === 'external description') {
      maxLength = 256;
    } else if (fieldName.toLowerCase() === 'greeting') {
      maxLength = 2048;
    }
    
    // Validate length if we have a known limit
    if (maxLength > 0 && valueToSubmit.length > maxLength) {
      setError(`${fieldName} cannot exceed ${maxLength} characters`);
      return;
    }
    
    // Validate if custom validator provided
    if (validate) {
      const validationError = validate(valueToSubmit);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    
    // Start loading state
    setIsLoading(true);
    setError('');
    
    try {
      // Call API and update local state on success
      const result = await onUpdate(valueToSubmit);
      
      // If API returns a new value, use it to update our state
      if (result && typeof result === 'object') {
        // Check if the result has the field we're updating
        const fieldKey = Object.keys(result).find(
          key => key.toLowerCase() === fieldName.toLowerCase() ||
                key.toLowerCase() === fieldName.replace(/\s+/g, '_').toLowerCase()
        );
        
        if (fieldKey && result[fieldKey] !== undefined) {
          setValue(result[fieldKey]);
          previousInitialValueRef.current = result[fieldKey];
        } else {
          // No matching field found, just use what we submitted
          previousInitialValueRef.current = valueToSubmit;
        }
      } else {
        // No structured result, just use what we submitted
        previousInitialValueRef.current = valueToSubmit;
      }
      
      // Exit edit mode
      setIsEditing(false);
    } catch (err: any) {
      // Detailed error handling
      const errorMessage = err.response?.data?.detail || 
                          err.message || 
                          `Failed to update ${fieldName}`;
      console.error(`Failed to update ${fieldName}:`, err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [value, validate, onUpdate, fieldName]);

  return {
    isEditing,
    setIsEditing,
    value,
    setValue: setValueAndLog,
    isLoading,
    error,
    startEdit,
    handleSubmit,
    isVisible,
    resetField
  };
}; 